export default (() =>
    class extends wealthify.componentBases.Field {

        constructor (config = {}) {
            const styleSelector = 'c-field-selection-base';

            const sharedTemplate = `
                <div class="${styleSelector}__input-container" data-inputcontainer></div>
                <p class="${styleSelector}__label-sub" data-fieldlabelsub></p>
                <div class="${styleSelector}__message-container">
                    <w-icon class="${styleSelector}__state-icon u-float--left" size="200" data-statusicon></w-icon>
                    <w-message class="${styleSelector}__message" aria-live="assertive" data-fieldmessage></w-message>
                </div>
                <span data-statusaria></span>`;

            const singleInputTemplate = `
                <div class="${styleSelector}__wrapper" data-root>
                    <div data-fieldlabelcontainer>
                        <label class="${styleSelector}__label" data-fieldlabel></label>
                    </div>
                    <p data-fieldlabelpre></p>

                    ${sharedTemplate}
                </div>`;

            const multipleInputTemplate = `
                <fieldset class="${styleSelector}__wrapper" data-fieldset data-root>
                    <legend class="u-margin__bottom-mobile--0" data-fieldlegend>
                        <div data-fieldlabelcontainer>
                            <p class="${styleSelector}__label" data-fieldlabel></p>
                        </div>
                        <p class="${styleSelector}__label-pre" data-fieldlabelpre></p>
                    </legend>

                    ${sharedTemplate}
                </fieldset>`;

            const defaultConfig = {
                hasChildren: true,
                isFieldInputConstructionDelayed: true
            };

            const templateHTML = config.canHaveMultipleInputs ? multipleInputTemplate : singleInputTemplate;

            let cacheDomBase = ['data-fieldlabelcontainer', 'data-fieldlabel', 'data-fieldlabelpre', 'data-fieldlabelsub', 'data-inputcontainer', 'data-fieldmessage', 'data-statusaria', 'data-statusicon'];

            if (!config.templateHTML) {
                defaultConfig.templateHTML = templateHTML;
            }

            if (config.cacheDom) {
                cacheDomBase = cacheDomBase.concat(config.cacheDom);
            }

            defaultConfig.cacheDom = cacheDomBase;

            Object.assign(config, defaultConfig);
            super(config);

            this.styleSelectors.add(styleSelector);

            this.onConnectedCallback = () => {
                this.#addFieldInputContent();
                this.deprecatedAttachAdditionalProps(this.selectionProps);
                this.addEventListener(this.events.DisabledToggled.name, this.#reinstateItemisedDisabledStates);
            };
        }

        // Public

        // Default implementation for use with templateProp and multi input instances, can be
        // overriden on the instance.
        addFieldInputElement () {
            const fragmentElement = document.createDocumentFragment();

            let template = this.constructor.template;

            this.options?.forEach((option, index) => {
                if (this.templateProp) {
                    // certain aspects of custom templates, such as
                    // product icons, can vary between options, so we
                    // need to rebuild the template for each option
                    template = this.buildCustomTemplate(option);
                }

                const itemContainer = document.createElement('label');
                const item = new DOMParser().parseFromString(template, 'text/html').body.firstElementChild;
                const fieldInput = item.querySelector('[data-fieldinput]');
                const label = item.querySelector('[data-fieldinputlabel]');


                itemContainer.setAttribute('data-item-container', '');
                itemContainer.htmlFor = `${this.name}-input-${index + 1}`;
                itemContainer.appendChild(item);

                if (!fieldInput) {
                    wealthify.error.throwError(this.tagName, 'Ensure the template contains an element with the `data-fieldinput` attribute');
                }

                if (!label) {
                    wealthify.error.throwError(this.tagName, 'Ensure the template contains an element with the `data-fieldinputlabel` attribute');
                }

                this.styleSelectors.forEach(styleSelector => {
                    itemContainer.classList.add(`${styleSelector}__item-container`);
                });

                if (this.templateProp != undefined) {
                    // ensure custom template items have the necessary attributes
                    this.styleSelectors.forEach(styleSelector => {
                        itemContainer.classList.add(`${styleSelector}__item-container--custom`, `${styleSelector}__item-container--wrapping-label`);
                        fieldInput.classList.add(`${styleSelector}__item-input`);
                        label.classList.add(`${styleSelector}__item-label`);
                    });

                    if (!this.dom.fieldinput) {
                        this.dom.fieldinput = [];
                    }

                    if (!this.dom.itemContainer) {
                        this.dom.itemContainer = [];
                    }

                    this.dom.fieldinput.push(fieldInput);
                    this.dom.itemContainer.push(itemContainer);
                }

                label.innerHTML = option.label;
                fieldInput.value = option.value;

                if (this.value === option.value) {
                    fieldInput.checked = true;
                    itemContainer.classList.add(`${this.styleSelector}__item-container--selected`);
                }

                fragmentElement.appendChild(itemContainer);
            });

            this.dom.inputcontainer.appendChild(fragmentElement);
        }

        // Instance properties
        selectionProps = Object.freeze([
            {
                class: new wealthify.Prop('options', {
                    type: wealthify.propTypes.Array(),
                    validators: [wealthify.propValidators.ArrayObjectContainsKeys(this.constructor.requiredItemsProperties)]
                }),
                callback: () => this.#addFieldInputContent()
            },
            {
                class: new wealthify.Prop('template-prop'),
                callback: () => this.#reinitialiseWithCustomTemplate()
            },
            {
                class: new wealthify.Prop('disabled-options'),
                callback: () => this.#disableOptions()
            }
        ]);

        static get requiredItemsProperties () {
            return [
                'label',
                'value'
            ];
        }

        buildCustomTemplate(option) {
            let customTemplate = this.templateProp;

            for (const [key, value] of Object.entries(option.extras)) {
                customTemplate = customTemplate.replaceAll(`{{${key}}}`, value);
            }

            return customTemplate;
        }

        // Private

        #addFieldInputContent () {
            // Either invoked by options callback or after component construction

            // The following method should, where applicable, add the bare element e.g. <select>
            // so that the options can be added later by callback
            // and so that the entire life cycle uses the same element.
            // If the options are the input, e.g. radios, checkboxes,
            // then component won't show until options are added
            this.addFieldInputElement();

            if (this.dom.fieldinput && !this.isInstanceLoaded ) {
                // Only dispatch construction method if input element has been added
                this.events.InstanceLoaded.dispatch();

                // Formatting of input must be done afterward
                // so that the field props are available
                this.formatFieldInputContent();
            }
        }

        #reinitialiseWithCustomTemplate () {
            if (!this.options && this.dom.inputcontainer.children.length === 0) {
                // Will be constructed by options callback
                return;
            }

            // Clear existing options constructed without template
            wealthify.dom.removeAllParentsChildren(this.dom.inputcontainer);
            // It's a requirement to clear out any items
            // that may have been cached with the initial construction
            // (prior to the custom template being used)
            this.dom.fieldinput = [];
            this.dom.itemContainer = [];

            this.addFieldInputElement();
            this.formatFieldInputContent();

            // Re-add itemised disabled states
            // as this would have been done on first load
            this.#disableOptions();

            // Re-fire callback for is-label-hidden callback
            // To add aria-label to inputs if required
            this.props.isLabelHidden.onChangeCallback();
        }

        #reinstateItemisedDisabledStates () {
            if (!this.disabledprop && this.disabledOptions) {
                this.#disableOptions(true);
            }
        }

        #disableOptions (isReturningFromGlobalDisable = false) {
            const disabledOptionIndices = this.disabledOptions
                ? this.disabledOptions.split(',').map(index => Number.parseInt(index))
                : '';

            this.dom.itemContainer.forEach((item, index) => {
                const input = this.dom.fieldinput[index];
                const isDisabled = disabledOptionIndices.includes(index);

                if (isDisabled) {
                    input.setAttribute('disabled', 'true');
                } else {
                    input.removeAttribute('disabled');
                }

                this.styleSelectors.forEach(styleSelector => {
                    item.classList[isDisabled ? 'add' : 'remove'](`${styleSelector}__item-container--disabled`);
                });

                if (!isReturningFromGlobalDisable) {
                    this.events.DisabledToggled.dispatch(isDisabled, true, item);
                }
            });

        }
    }
);